import { EMPTY_RECT, MaterialNames } from 'Constants';
import type { MaterialPattern } from 'Pattern';
import {
  BoardDetails,
  BoardType,
  Coord,
  CoordCtr,
  Dimensions2D,
  Dimensions3D,
  Direction,
  Material,
  NormalizedRotation,
  OneArray,
  Rectangle,
  Renderable,
  RenderableMetadata,
  ScaledCoord,
  Settings,
  TwoArray,
  Units,
} from 'Types';

export const numberFormatter = Intl.NumberFormat('en-US', {
  maximumFractionDigits: 3,
});

export const priceFormatter = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

export function degToRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}

export function displayNumber(number: number, unit?: string): string {
  return numberFormatter.format(number) + (unit || '');
}

export function getDisplayLengthInUnits(
  units: Units
): (number: number) => string {
  return (number) => displayNumber(number, units);
}

export function displayPrice(number: number): string {
  return priceFormatter.format(number);
}

export function displayPercent(number: number): string {
  return displayNumber(100 * number, '%');
}

export function displayDegrees(number: number): string {
  return displayNumber(number, '\u00B0');
}

export function bumpNumberUp(number: number, bump: number): number {
  return number + bump - (number % bump);
}

export function bumpNumberDown(number: number, bump: number): number {
  if (number % bump === 0) {
    return Math.max(0, number - bump);
  }
  return Math.max(0, number - bump + (number % bump));
}

export function getRowDrawingSkewing(rotate: number): number {
  return Math.tan(getRotateRad(rotate));
}

export function getRowDrawingOffset(rotate: number, rect: Rectangle) {
  const singleOffset = rect.height * getRowDrawingSkewing(rotate);
  const topOffset = rect.top * getRowDrawingSkewing(rotate);

  return rotate > 0 ? -topOffset - singleOffset : -topOffset;
}

export function getRotateRad(rotate: number): number {
  return degToRad(rotate);
}

export function descaleCoord(point: ScaledCoord, scale: number): Coord {
  return CoordCtr({ top: point.top / scale, left: point.left / scale });
}

export function rotateCoord(
  coord: ScaledCoord,
  rotate: NormalizedRotation,
  dimensions: Dimensions2D
): ScaledCoord {
  if (rotate === 0) {
    return coord;
  } else if (rotate === 90) {
    return {
      top: dimensions.height - coord.left,
      left: coord.top,
      isScaled: coord.isScaled,
    };
  } else if (rotate === 180) {
    return {
      top: dimensions.height - coord.top,
      left: dimensions.width - coord.left,
      isScaled: coord.isScaled,
    };
  } else if (rotate === 270) {
    return {
      top: coord.left,
      left: dimensions.width - coord.top,
      isScaled: coord.isScaled,
    };
  }

  return coord;
}

const normalizedRotations = new Set([0, 90, 180, 270]);
export function normalizeRotation(rotation: number): NormalizedRotation {
  const normalizedRotation =
    rotation > 0 ? rotation % 360 : (rotation % 360) + 360;

  return normalizedRotations.has(normalizedRotation)
    ? (normalizedRotation as NormalizedRotation)
    : 0;
}

export function scaleRectangle(rect: Rectangle, scale: number): Rectangle {
  return {
    height: rect.height * scale,
    width: rect.width * scale,
    top: rect.top * scale,
    left: rect.left * scale,
  };
}

export function scaleDimensions(
  dimensions: Dimensions2D,
  scale: number
): Dimensions2D {
  return {
    height: dimensions.height * scale,
    width: dimensions.width * scale,
  };
}

export function dimensionsToFBM(dimensions: Dimensions3D, units: Units) {
  switch (units) {
    case Units.IN:
      return (
        (dimensions.length * dimensions.width * dimensions.thickness) / 144
      );
    case Units.MM:
      return (
        (dimensions.length * dimensions.width * dimensions.thickness) /
        16387.064 /
        144
      );
  }
}

export function detailsToPrice(
  details: BoardDetails,
  settings: Settings,
  units: Units
): number {
  return sumBy(details.idealStartingStock, ({ stock }) =>
    sumBy(stock, ({ dimensions, material }) => {
      const materialCost = settings.materialCosts[material as Material];
      return materialCost && dimensions
        ? dimensionsToFBM(dimensions, units) * materialCost.price
        : 0;
    })
  );
}

export function getSettingsInUnits(settings: Settings, units: Units): Settings {
  return {
    ...settings,
    kerf: convertNumber(settings.kerf, settings.units, units),
    planeBuffer: convertNumber(settings.planeBuffer, settings.units, units),
  };
}

export function convertNumber(
  value: number,
  fromUnits: Units,
  toUnits: Units
): number {
  switch (fromUnits) {
    case Units.IN: {
      switch (toUnits) {
        case Units.IN: {
          return value;
        }
        case Units.MM: {
          return value * 25.4;
        }
      }
      break;
    }
    case Units.MM:
      switch (toUnits) {
        case Units.IN: {
          return value / 25.4;
        }
        case Units.MM: {
          return value;
        }
      }

      break;
  }
}

export function findRenderableFromCoord<T extends BoardType>(
  coord: Coord,
  renderables: Array<Renderable<T>>
): number {
  renderables.reverse();

  const index = renderables.findIndex((renderable) => {
    const { rect, rotate } = renderable;

    if (coord.top >= rect.top && coord.top < rect.top + rect.height) {
      const offset =
        rotate == null
          ? 0
          : getRowDrawingOffset(rotate, {
              top: 0,
              height: rect.top + rect.height - coord.top,
              width: 0,
              left: 0,
            });

      if (
        coord.left - offset >= rect.left &&
        coord.left - offset < rect.left + rect.width
      ) {
        return true;
      }
    }

    return false;
  });

  renderables.reverse();

  return renderables.length - 1 - index;
}

export function fillRenderablesAt<T extends BoardType>(
  renderables: Array<Renderable<T>>,
  pattern: MaterialPattern,
  topOffset: number,
  leftOffset: number,
  minorSize: number,
  direction: Direction,
  getSelectionMetadata: (args: {
    fragmentIndex: number;
    renderableIndex: number;
  }) => RenderableMetadata<T>
) {
  let fragmentOffset = 0;

  for (let fragmentIndex = 0; fragmentIndex < pattern.length; ++fragmentIndex) {
    const { size, key } = pattern[fragmentIndex];

    const renderable: Renderable<T> = {
      material: key,
      // This will be overwritten below
      rect: EMPTY_RECT,
      metadata: getSelectionMetadata({
        fragmentIndex,
        renderableIndex: renderables.length,
      }),
      selectionSizeProp: direction === Direction.H ? 'width' : 'height',
    };

    renderables.push(renderable);

    if (direction === Direction.H) {
      // Horizontal
      renderable.rect = {
        top: topOffset,
        left: leftOffset + fragmentOffset,
        height: minorSize,
        width: size,
      };
    } else {
      // Vertical
      renderable.rect = {
        top: topOffset + fragmentOffset,
        left: leftOffset,
        height: size,
        width: minorSize,
      };
    }

    fragmentOffset += size;
  }
}

export function flippedIndex(
  length: number,
  index: number,
  isFlipped: boolean
): number {
  return isFlipped ? length - index - 1 : index;
}

export function isFlipped(index: number, flip: boolean): boolean {
  return flip && index % 2 === 1;
}

export function getMaterialName(key: string | null): string {
  return MaterialNames[key as Material] || '(unspecified)';
}

export function nullThrows<T>(arg: T | null | undefined): T {
  if (arg === null || arg === undefined) {
    throw new Error('unexpected null or undefined');
  }

  return arg;
}

export function scaleNumber(n: number, scaleFactor: number) {
  return Math.round(n * scaleFactor * 1000) / 1000;
}

export function loadScript(src: string, onload: () => void) {
  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  script.crossOrigin = '';
  script.onload = onload;

  document.head.append(script);

  return script;
}

export function isDev() {
  try {
    const params = new window.URLSearchParams(window.location.search);
    const param = params.get('__DEV__');

    if (param != null) {
      return !!+param;
    }

    // @ts-ignore
    const devtools = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
    const renderer = Array.from(
      devtools?.renderers?.values() ?? []
    )[0] as null | { bundleType: 0 | 1 };

    if (renderer?.bundleType === 1) {
      return true;
    }
  } catch (error) {}

  return false;
}

export function nonNullable<T>(value: T): value is NonNullable<T> {
  return value != null;
}

export function randomBool(): boolean {
  return Boolean(randomInt(0, 1));
}

export function randomInt(low: number, high: number): number {
  return Math.floor(Math.random() * (high + 1 - low)) + low;
}

export function sample<T>(items: OneArray<T>): T {
  return items[randomInt(0, items.length - 1)];
}

export function sumBy<T>(items: Array<T>, getNumber: (item: T) => number) {
  let total = 0;
  for (const item of items) {
    total += getNumber(item);
  }
  return total;
}

export function maxBy<T>(items: Array<T>, getNumber: (item: T) => number) {
  let max = 0;
  for (const item of items) {
    const n = getNumber(item);
    if (n > max) {
      max = n;
    }
  }
  return max;
}

export function memoize<T extends any[], R>(
  fn: (...args: T) => R,
  serializer: (...args: T) => string
): (...args: T) => R {
  const cache = new Map();

  return (...args: T) => {
    const key = serializer(...args);
    if (cache.has(key)) {
      return cache.get(key);
    }

    const result = fn(...args);
    cache.set(serializer(...args), result);

    return result;
  };
}

export function cloneDeep<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

export function isEqual(a: any, b: any): boolean {
  if (typeof a !== typeof b) {
    return false;
  }

  if (Object(a) === a && Object(b) === b) {
    return a === b;
  }

  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

export function sortBy<T extends object>(
  array: Array<T>,
  keys: [keyof T, ...(keyof T)[]]
): Array<T> {
  return [...array].sort((a, b) => {
    for (const key of keys) {
      if (a[key] === b[key]) {
        continue;
      }

      return a[key] < b[key] ? -1 : 1;
    }

    return 1;
  });
}

export function groupBy<T extends object, K extends keyof T>(
  array: Array<T>,
  key: K
): Map<T[K], Array<T>> {
  const result = new Map<T[K], Array<T>>();
  for (const item of array) {
    const groupKey = item[key];
    const group = result.get(groupKey) ?? [];
    group.push(item);
    result.set(groupKey, group);
  }
  return result;
}

export function mapMapValues<K, V1, V2>(
  map: Map<K, V1>,
  mapFn: (key: K, value: V1) => V2
): Map<K, V2> {
  const result = new Map<K, V2>();
  for (const [key, value] of map) {
    result.set(key, mapFn(key, value));
  }
  return result;
}

export function mapMapEntries<K, V, R>(
  map: Map<K, V>,
  mapFn: (key: K, value: V) => R
): Array<R> {
  const result = [];
  for (const [key, value] of map) {
    result.push(mapFn(key, value));
  }
  return result;
}

export function range(
  min: number,
  max: number,
  step: number = 1
): OneArray<number> {
  const result: OneArray<number> = [min];
  let curr = min;
  while (curr < max) {
    result.push(curr + step);
    curr = curr + step;
  }
  return result;
}

export function without<T>(
  items: TwoArray<T>,
  filterFn: (item: T) => boolean
): OneArray<T>;
export function without<T>(
  items: OneArray<T>,
  filterFn: (item: T) => boolean
): Array<T>;
export function without<T>(
  items: Array<T>,
  filterFn: (item: T) => boolean
): Array<T> {
  return items.filter(filterFn);
}

export function withKerf(size: number, n: number, kerf: number) {
  return size * n + (n - 1) * kerf;
}

export function withKerfAndBuffer(
  size: number,
  n: number,
  { kerf, planeBuffer }: Settings
) {
  return (size + planeBuffer) * n + (n - 1) * kerf;
}
